/**
 * Step 5 — Notifications [TYPE A — Recommended]
 *
 * Embeds NotificationsPage directly — same screen as Settings → Notifications.
 * The page runs in embedded mode, so it renders neither its PageHeader nor its
 * fixed save bar; Save & Continue calls the save function the page registers,
 * so all existing notification save logic (validation, toast, rowVersion) runs
 * as normal.
 *
 * Visibility gate: canManageNotifications() from usePermissions().
 * If false, skip this step entirely.
 */

import { useRef, useCallback } from 'react';
import { NotificationsPage } from '@/components/Pages/NotificationsPage';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, SkipForward } from 'lucide-react';

interface Step5NotificationsProps {
  onNext: (status: 'completed' | 'skipped') => void;
  onBack: () => void;
}

export function Step5Notifications({ onNext, onBack }: Step5NotificationsProps) {
  const saveRef = useRef<(() => Promise<void>) | null>(null);

  const handleRegisterSave = useCallback((save: () => Promise<void>) => {
    saveRef.current = save;
  }, []);

  const handleNext = useCallback(async () => {
    try {
      await saveRef.current?.();
    } finally {
      // Always advance — the page surfaces its own error toast on failure.
      onNext('completed');
    }
  }, [onNext]);

  const handleSkip = useCallback(() => {
    onNext('skipped');
  }, [onNext]);

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Embedded notifications panel */}
      <div className="flex-1 min-h-0 overflow-hidden px-3 pt-3 pb-1">
        <div
          className="flex flex-col h-full min-h-0 border border-border rounded-lg bg-white overflow-hidden"
          style={{ transform: 'translateZ(0)', willChange: 'transform' }}
        >
          <div className="flex-1 min-h-0 overflow-y-auto p-4">
            <NotificationsPage embedded onRegisterSave={handleRegisterSave} />
          </div>
        </div>
      </div>

      {/* Wizard bottom bar */}
      <div className="shrink-0 border-t bg-white px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground max-w-xl">
            Set up reminders for submission deadlines, approval chasers, and manager digests
            before your team submits their first timesheet.
          </p>
          <div className="flex items-center gap-3 shrink-0">
            <Button variant="ghost" size="sm" onClick={onBack} className="gap-1">
              <ChevronLeft className="h-4 w-4" />
              Back
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSkip}
              className="gap-1 text-muted-foreground"
            >
              <SkipForward className="h-4 w-4" />
              Skip for now
            </Button>
            <Button
              size="sm"
              onClick={handleNext}
              className="gap-1 bg-[#00796B] hover:bg-[#00695C] text-white"
            >
              Save & Continue
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
